/**
 * O1 — Match metadata strip.
 *
 * Top-anchored DOM strip showing agent identity + tick progress + (when
 * available) match score and ratings. Sibling to the Canvas, not
 * inside it — easier theming, accessible, OBS-friendly.
 *
 * Layout (left → right):
 *   [Agent A name · model · rating]  [tick N / Nmax · score]  [Agent B …]
 *
 * Score + ratings are optional; the strip degrades gracefully to just
 * agent names + tick when those aren't passed.
 */

import type { CSSProperties } from "react";
import type { ParsedBattle } from "@pilot-protocol/replay-parser";
import { Pip } from "@pilot-protocol/theme/chrome";
import { Panel } from "./Panel";

export interface MatchScore {
  /** Wins for side A in the current match. */
  winsA: number;
  /** Wins for side B. */
  winsB: number;
  /** Best-of-N target — e.g. 3 means race to 2. */
  bestOf: number;
}

export interface MatchMetadataStripProps {
  battle: ParsedBattle;
  /** Current playback tick. */
  tick: number;
  /** Best-of-N match score (optional — not on a single battle archive). */
  score?: MatchScore;
  /** Glicko rating for side A (optional — comes from tournament context). */
  ratingA?: number;
  /** Glicko rating for side B. */
  ratingB?: number;
  /** Archetype hint per side (optional — passed by consumer). */
  archetypeA?: string;
  archetypeB?: string;
  /** Hide entirely. */
  hidden?: boolean;
}

const wrapperStyle: CSSProperties = {
  position: "absolute",
  top: "var(--pp-edge-gutter)",
  left: "var(--pp-edge-gutter)",
  right: "var(--pp-edge-gutter)",
  pointerEvents: "none",
  userSelect: "none",
};

const contentStyle: CSSProperties = {
  height: "var(--pp-strip-height)",
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  alignItems: "center",
  padding: "0 16px",
  gap: "var(--pp-gap)",
  color: "var(--pp-text)",
  fontFamily: "var(--pp-font-family)",
  fontSize: "var(--pp-font-md)",
  fontWeight: "var(--pp-weight-normal)",
};

const sideStyle = (align: "start" | "end"): CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  justifySelf: align,
  minWidth: 0,
});

const nameStyle: CSSProperties = {
  fontWeight: "var(--pp-weight-bold)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: 200,
};

const versionStyle: CSSProperties = {
  color: "var(--pp-text-muted)",
  fontSize: "var(--pp-font-sm)",
};

const archetypeStyle: CSSProperties = {
  textTransform: "uppercase",
  letterSpacing: "var(--pp-tracking-caps)",
  color: "var(--pp-text-muted)",
  fontSize: "var(--pp-font-sm)",
};

const ratingStyle: CSSProperties = {
  padding: "1px 6px",
  border: "1px solid var(--pp-border)",
  fontSize: "var(--pp-font-sm)",
  fontVariantNumeric: "tabular-nums",
  letterSpacing: "var(--pp-tracking-caps)",
};

const centerStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2,
  fontVariantNumeric: "tabular-nums",
};

const tickStyle: CSSProperties = {
  fontWeight: "var(--pp-weight-bold)",
  fontSize: "var(--pp-font-md)",
  letterSpacing: "var(--pp-tracking-caps)",
};

const scoreStyle: CSSProperties = {
  display: "flex",
  gap: 6,
  alignItems: "center",
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
};

export function MatchMetadataStrip({
  battle,
  tick,
  score,
  ratingA,
  ratingB,
  archetypeA,
  archetypeB,
  hidden = false,
}: MatchMetadataStripProps) {
  if (hidden) return null;
  const meta = battle.openMeta;
  const nameA = meta?.agent_a?.name ?? "Agent A";
  const versionA = meta?.agent_a?.version;
  const nameB = meta?.agent_b?.name ?? "Agent B";
  const versionB = meta?.agent_b?.version;
  const maxTick = Math.max(0, battle.states.length - 1);
  const accentA = "var(--pp-accent-a)";
  const accentB = "var(--pp-accent-b)";

  return (
    <div style={wrapperStyle}>
      <Panel variant="solid" ariaLabel="Match metadata">
        <div style={contentStyle}>
          <div style={sideStyle("start")}>
            <Pip color={accentA} filled size={10} glow />
            <span style={nameStyle} title={nameA}>
              {nameA}
            </span>
            {versionA ? <span style={versionStyle}>·{versionA}</span> : null}
            {archetypeA ? <span style={archetypeStyle}>{archetypeA}</span> : null}
            {typeof ratingA === "number" ? (
              <span style={ratingStyle}>{Math.round(ratingA)}</span>
            ) : null}
          </div>
          <div style={centerStyle}>
            <span style={tickStyle}>
              T{tick.toString().padStart(2, "0")} / {maxTick.toString().padStart(2, "0")}
            </span>
            {score ? <ScoreDots score={score} accentA={accentA} accentB={accentB} /> : null}
          </div>
          <div style={sideStyle("end")}>
            {typeof ratingB === "number" ? (
              <span style={ratingStyle}>{Math.round(ratingB)}</span>
            ) : null}
            {archetypeB ? <span style={archetypeStyle}>{archetypeB}</span> : null}
            {versionB ? <span style={versionStyle}>·{versionB}</span> : null}
            <span style={nameStyle} title={nameB}>
              {nameB}
            </span>
            <Pip color={accentB} filled size={10} glow />
          </div>
        </div>
      </Panel>
    </div>
  );
}

interface ScoreDotsProps {
  score: MatchScore;
  accentA: string;
  accentB: string;
}

function ScoreDots({ score, accentA, accentB }: ScoreDotsProps) {
  // Race to ceil(bestOf/2). Show one dot per win-slot per side.
  const target = Math.max(1, Math.ceil(score.bestOf / 2));
  const slotsA = Array.from({ length: target }, (_, i) => i < score.winsA);
  const slotsB = Array.from({ length: target }, (_, i) => i < score.winsB);
  return (
    <div style={scoreStyle} aria-label={`Score ${score.winsA} to ${score.winsB}`}>
      <span style={{ display: "flex", gap: 3 }}>
        {slotsA.map((won, i) => (
          <Pip key={`a-${i}`} color={accentA} filled={won} />
        ))}
      </span>
      <span style={{ opacity: 0.5 }}>·</span>
      <span style={{ display: "flex", gap: 3 }}>
        {slotsB.map((won, i) => (
          <Pip key={`b-${i}`} color={accentB} filled={won} />
        ))}
      </span>
    </div>
  );
}
